import React from 'react'
import { withFirebase } from '../../Fb'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { summarizeTestResult, translateBackendMessage } from '../../utils/membershipsync'

// One source of a sync integration (one CSV file or one Tennisclub club). A venue may have several.
// sourceId null + config null = a new source not saved yet (a draft): Tallenna creates it, Peruuta
// drops it. Which fields it edits, how they are validated and whether it can be dry-run tested come
// from props; the config itself lives in the backend (membershipsync-call), see
// docs/features/membership-sync.md.
class MembershipSyncCard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      form: this.formFromConfig(props.config),
      saved: !!props.sourceId,
      working: false,
      errors: [],
      notice: null,
      confirmRemove: false,
      testResult: null,
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.config !== this.props.config && this.props.config) {
      this.setState({ form: this.formFromConfig(this.props.config), saved: true })
    }
  }

  formFromConfig = (config) => {
    const form = {}
    this.props.fields.forEach((f) => {
      form[f.key] = (config && config[f.key]) || f.defaultValue || ''
    })
    return form
  }

  setField = (key, value) => {
    this.setState({ form: { ...this.state.form, [key]: value }, saved: false, notice: null })
  }

  onSave = async () => {
    const errors = this.props.validate(this.state.form)
    if (errors.length > 0) {
      this.setState({ errors })
      return
    }
    this.setState({ working: true, errors: [], notice: null })
    const result = await this.props.firebase.updateMembershipSync(
      this.props.integration,
      this.props.sourceId,
      this.props.pickFields(this.state.form)
    )
    if (result && result.error) {
      this.setState({ working: false, errors: ['Tallennus epäonnistui: ' + translateBackendMessage(result.message)] })
      return
    }
    // The backend drops Cintoia's credentials when the url moves to another server or the club
    // changes; they belong to the target they were set for.
    const notice = result && result.credentialsCleared
      ? { severity: 'warning', text: 'Asetukset tallennettu. ' + this.props.credentialsClearedText }
      : { severity: 'success', text: 'Asetukset tallennettu. Synkronointi on käytössä.' }
    this.setState({ working: false, saved: true, notice })
    this.props.onChanged()
  }

  onRemove = async () => {
    this.setState({ working: true, confirmRemove: false, errors: [], notice: null })
    const result = await this.props.firebase.updateMembershipSync(this.props.integration, this.props.sourceId, null)
    if (result && result.error) {
      this.setState({ working: false, errors: ['Poisto epäonnistui: ' + translateBackendMessage(result.message)] })
      return
    }
    this.setState({ working: false })
    this.props.onChanged()
  }

  onTest = async () => {
    const errors = this.props.validate(this.state.form)
    if (errors.length > 0) {
      this.setState({ errors })
      return
    }
    this.setState({ working: true, errors: [], testResult: null })
    const result = await this.props.firebase.testMembershipSync(
      this.props.integration,
      this.props.sourceId,
      this.props.pickFields(this.state.form)
    )
    this.setState({ working: false, testResult: result })
  }

  renderField = (field) => {
    const value = this.state.form[field.key]
    if (field.type === 'membership') {
      const { memberships } = this.props
      return (
        <TextField
          key={field.key}
          select
          size='small'
          label={field.label}
          helperText={field.helper}
          value={memberships.some((m) => m.id === value) ? value : ''}
          onChange={(e) => this.setField(field.key, e.target.value)}
          fullWidth
        >
          {memberships.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>
      )
    }
    return (
      <TextField
        key={field.key}
        size='small'
        label={field.label}
        helperText={field.helper}
        value={value}
        onChange={(e) => this.setField(field.key, e.target.value)}
        fullWidth
      />
    )
  }

  renderTestResult = () => {
    const result = this.state.testResult
    if (!result) return null
    const lines = summarizeTestResult(result, {
      hasCredentials: !!(this.props.config && this.props.config.hasCredentials),
    })
    const header = Array.isArray(result.header) ? result.header : []
    const sample = Array.isArray(result.sample) ? result.sample : []
    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant='subtitle1'>Testin tulos</Typography>
        {lines.map((line, i) => (
          <Alert key={i} severity={line.severity}>
            {line.text}
          </Alert>
        ))}
        {header.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Typography variant='caption' color='textSecondary'>
              Otsikkorivi ja ensimmäiset rivit
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {header.map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 'bold' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sample.map((row, r) => (
                  <TableRow key={r}>
                    {(Array.isArray(row) ? row : []).map((cell, c) => (
                      <TableCell key={c}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    )
  }

  render() {
    const { title, config, sourceId, testable, notTestableText } = this.props
    const isDraft = !sourceId
    return (
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant='h6'>{title}</Typography>
            <Chip
              size='small'
              color={isDraft ? 'default' : 'success'}
              label={isDraft ? 'Uusi, ei tallennettu' : 'Käytössä'}
            />
            {config && config.hasCredentials && <Chip size='small' variant='outlined' label='Tunnukset asetettu' />}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
            {this.props.fields.map(this.renderField)}
          </Box>

          {this.state.errors.length > 0 && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {this.state.errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Alert>
          )}
          {this.state.notice && (
            <Alert severity={this.state.notice.severity} sx={{ mt: 2 }}>
              {this.state.notice.text}
            </Alert>
          )}
          {!testable && (
            <Alert severity='info' sx={{ mt: 2 }}>
              {notTestableText}
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant='contained' disabled={this.state.saved || this.state.working} onClick={this.onSave}>
              Tallenna
            </Button>
            {testable && (
              <Button variant='outlined' disabled={this.state.working} onClick={this.onTest}>
                Testaa (ei tallenna mitään)
              </Button>
            )}
            {isDraft ? (
              <Button variant='outlined' disabled={this.state.working} onClick={this.props.onCancel}>
                Peruuta
              </Button>
            ) : (
              <Button
                variant='outlined'
                color='error'
                disabled={this.state.working}
                onClick={() => this.setState({ confirmRemove: true })}
              >
                Poista lähde
              </Button>
            )}
          </Box>

          {this.renderTestResult()}

          <Dialog open={this.state.confirmRemove} onClose={() => this.setState({ confirmRemove: false })}>
            <DialogTitle>Poista lähde: {title}</DialogTitle>
            <DialogContent>
              <Typography>
                Lähdettä ei enää synkronoida ja sen asetukset poistetaan. Sen kautta jo annetut
                jäsenyydet jäävät voimaan.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ confirmRemove: false })}>Peruuta</Button>
              <Button color='error' onClick={this.onRemove}>
                Poista lähde
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    )
  }
}

export default withFirebase(MembershipSyncCard)
